export type JsonApiErrorItem = {
  id?: string;
  status?: string;
  code?: string;
  title?: string;
  detail?: string;
  source?: {
    pointer?: string;
  };
};

export type SessionRecoveryErrorCategory = 'EXPIRED' | 'NOT_FOUND' | 'UNAUTHORIZED' | 'NETWORK' | 'UNKNOWN';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function resolveErrorPayload(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) {
    return null;
  }

  const response = error.response;
  if (isRecord(response) && 'data' in response) {
    const data = response.data;
    if (isRecord(data)) {
      return data;
    }
  }

  if ('payload' in error && isRecord(error.payload)) {
    return error.payload;
  }

  return error;
}

function readJsonApiErrors(payload: Record<string, unknown> | null): JsonApiErrorItem[] {
  if (!payload) {
    return [];
  }

  const errors = payload.errors;
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.filter((item): item is JsonApiErrorItem => isRecord(item));
}

function normalizeFieldName(field: string): string {
  return field.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

export function extractJsonApiErrorCode(error: unknown): string | null {
  const payload = resolveErrorPayload(error);
  const errors = readJsonApiErrors(payload);
  const code = errors.find((item) => isNonEmptyString(item.code))?.code;
  if (code) {
    return code;
  }

  if (payload && isNonEmptyString(payload.code)) {
    return payload.code;
  }

  const metaCode =
    payload && isRecord(payload.meta) && isNonEmptyString(payload.meta.code) ? payload.meta.code : null;
  if (metaCode) {
    return metaCode;
  }

  return null;
}

export function extractErrorStatus(error: unknown): number | undefined {
  if (isRecord(error)) {
    const status = error.status;
    if (typeof status === 'number') {
      return status;
    }
    if (isNonEmptyString(status) && /^\d{3}$/.test(status)) {
      return Number(status);
    }

    const response = error.response;
    if (isRecord(response)) {
      const responseStatus = response.status;
      if (typeof responseStatus === 'number') {
        return responseStatus;
      }
      if (isNonEmptyString(responseStatus) && /^\d{3}$/.test(responseStatus)) {
        return Number(responseStatus);
      }
    }
  }

  const payload = resolveErrorPayload(error);
  if (payload) {
    if (typeof payload.status === 'number') {
      return payload.status;
    }
    if (isNonEmptyString(payload.status) && /^\d{3}$/.test(payload.status)) {
      return Number(payload.status);
    }
  }

  return undefined;
}

export function extractJsonApiErrorMessage(error: unknown, fallback: string): string {
  const payload = resolveErrorPayload(error);
  const errors = readJsonApiErrors(payload);

  if (errors.length > 0) {
    const detail = errors.find((item) => isNonEmptyString(item.detail))?.detail;
    if (detail) {
      return detail;
    }
    const title = errors.find((item) => isNonEmptyString(item.title))?.title;
    if (title) {
      return title;
    }
  }

  if (payload && isRecord(payload.errors)) {
    const values = Object.values(payload.errors);
    for (const value of values) {
      if (Array.isArray(value)) {
        const message = value.find(isNonEmptyString);
        if (message) {
          return message;
        }
      } else if (isNonEmptyString(value)) {
        return value;
      }
    }
  }

  if (payload) {
    if (isNonEmptyString(payload.message)) {
      return payload.message;
    }
    if (isNonEmptyString(payload.error)) {
      return payload.error;
    }
  }

  if (error instanceof Error && isNonEmptyString(error.message)) {
    return error.message;
  }

  return fallback;
}

export function extractJsonApiFieldErrors(error: unknown): Record<string, string> {
  const payload = resolveErrorPayload(error);
  if (!payload) {
    return {};
  }

  const errors = payload.errors;
  const fieldErrors: Record<string, string> = {};

  if (Array.isArray(errors)) {
    errors.forEach((entry) => {
      if (!isRecord(entry)) {
        return;
      }

      const pointer = isRecord(entry.source) ? entry.source.pointer : undefined;
      if (!isNonEmptyString(pointer)) {
        return;
      }

      const segments = pointer.split('/').filter(Boolean);
      const fieldName = segments[segments.length - 1];
      if (!fieldName) {
        return;
      }

      const message =
        (isNonEmptyString(entry.detail) ? entry.detail : undefined) ??
        (isNonEmptyString(entry.title) ? entry.title : undefined);
      if (!message) {
        return;
      }

      fieldErrors[normalizeFieldName(fieldName)] = message;
    });

    return fieldErrors;
  }

  if (isRecord(errors)) {
    Object.entries(errors).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        const message = value.find(isNonEmptyString);
        if (message) {
          fieldErrors[normalizeFieldName(field)] = message;
        }
      } else if (isNonEmptyString(value)) {
        fieldErrors[normalizeFieldName(field)] = value;
      }
    });
  }

  return fieldErrors;
}

export function categorizeSessionRecoveryError(error: unknown): SessionRecoveryErrorCategory {
  const code = extractJsonApiErrorCode(error)?.toUpperCase();
  const status = extractErrorStatus(error);

  if (status === 401 || status === 403) {
    return 'UNAUTHORIZED';
  }

  if (status === 410 || code === 'SSO_TOKEN_EXPIRED' || code === 'SESSION_EXPIRED') {
    return 'EXPIRED';
  }

  if (status === 404 || code === 'SSO_TOKEN_INVALID' || code === 'SESSION_NOT_FOUND') {
    return 'NOT_FOUND';
  }

  if (isRecord(error)) {
    const networkCode = isNonEmptyString(error.code) ? error.code.toUpperCase() : null;
    if (networkCode === 'ERR_NETWORK' || networkCode === 'ECONNABORTED') {
      return 'NETWORK';
    }
  }

  if (error instanceof TypeError && /network|fetch|load failed/i.test(error.message)) {
    return 'NETWORK';
  }

  if (status === undefined) {
    return 'NETWORK';
  }

  return 'UNKNOWN';
}
